import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { Button, useDisclosure } from '@heroui/react';
import { isScormProcessing, useScormUploadStepIds } from '@/features/StepScormUploads';
import {
  isVideoUploadInterrupted,
  mapLessonVideo,
  useVideoUploadStepIds,
} from '@/features/StepVideoUploads';
import {
  useGetTermsQuery,
  useGetModuleLessonsQuery,
  useGetLessonByModuleQuery,
  useGetModuleBaseInfoQuery,
  useDeleteLessonMutation,
  Lesson,
  Resubmission,
} from '@/entities/Courses';
import { Container } from '@/shared/ui/Container';
import { CourseBreadcrumbs } from '@/shared/ui/CourseBreadcrumbs';
import { CustomInput, SelectSort } from '@/shared/ui/Fields';
import { ContainerTable } from '@/shared/ui/AppTable';
import { Svg } from '@/shared/ui/Svg';
import { useDebounce } from '@/shared/lib/hooks/useDebounce';
import { useDocumentVisibility } from '@/shared/lib/hooks/useDocumentVisibility';
import { searchInputClassName } from '@/shared/config/searchInputClassName';
import SearchIcon from '@/shared/assets/icons/search.svg';
import { SectionLoader } from '@/shared/ui/Loader';
import { routerPath } from '@/shared/config/router';
import { MessageModal } from '@/shared/ui/Modal';
import { useToast } from '@/shared/lib/hooks/useToast';
import { useAdminAccess } from '@/shared/lib/adminAccess';
import { Divider } from '@/shared/ui/Divider';
import FilterIcon from '@/shared/assets/icons/filter.svg';
import TrashIcon from '@/shared/assets/icons/trash.svg';
import OpenedLockIcon from '@/shared/assets/icons/opened-lock.svg';
import MoveIcon from '@/shared/assets/icons/move.svg';
import { sessionsColumns, type ModuleSessionTableRow } from '../../config/sessionsColumns';
import { sessionsSortOptions } from '../../config/sortOptions';
import { AddSessionModal } from '../components/AddSessionModal';
import { EditSessionModal } from '../components/EditSessionModal';
import styles from './ModuleDetailView.module.scss';

// SCORM packages in processing and step videos in Cloudflare Stream
const CONTENT_STATUS_POLLING_MS = 5000;
// A video or SCORM upload that never finished and is not running in this tab changes rarely,
// if ever
const INTERRUPTED_UPLOAD_POLLING_MS = 60 * 1000;

type Session = Lesson | Resubmission;
type SessionRow = ModuleSessionTableRow;

interface ModuleDetailViewProps {
  courseId: number;
  intakeId: number;
  groupNumber: number;
  termId: number;
  moduleId: number;
}

export const ModuleDetailView = ({
  courseId,
  intakeId,
  groupNumber,
  termId,
  moduleId,
}: ModuleDetailViewProps) => {
  const navigate = useNavigate();
  const { canEntity } = useAdminAccess();
  const canSessionCreate = canEntity('session', 'create');
  const canSessionUpdate = canEntity('session', 'update');
  const canSessionDelete = canEntity('session', 'delete');
  // Отметка посещаемости сессии сохраняется bulk-созданием: бэкенд проверяет attendance.create
  const canMarkAttendance = canEntity('attendance', 'create');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 500);
  const [sessionsSort, setSessionsSort] = useState<string>('default');
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [isEditSessionModalOpen, setIsEditSessionModalOpen] = useState(false);
  const [selectedSessionForEdit, setSelectedSessionForEdit] = useState<Lesson | null>(null);
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onOpenChange: onDeleteModalOpenChange,
  } = useDisclosure();
  const [selectedSessionForDelete, setSelectedSessionForDelete] = useState<Lesson | null>(null);
  const [selectedSessions, setSelectedSessions] = useState<SessionRow[]>([]);
  const {
    isOpen: isBulkDeleteModalOpen,
    onOpen: onBulkDeleteModalOpen,
    onOpenChange: onBulkDeleteModalOpenChange,
  } = useDisclosure();
  const { toast } = useToast();
  const [deleteLesson, { isLoading: isDeleting }] = useDeleteLessonMutation();

  const { data: termsData, isLoading: isTermsLoading } = useGetTermsQuery({ intakeId });

  const term = useMemo(
    () => termsData?.data?.terms.find((t) => t.id === termId),
    [termsData?.data?.terms, termId],
  );

  const groupId = useMemo(() => {
    const group = termsData?.data?.groups.find((g) => g.number === groupNumber);
    return group?.id;
  }, [termsData?.data?.groups, groupNumber]);

  const lessonQueryArgs = {
    intakeModuleId: moduleId,
    lessonId: selectedSessionForEdit?.id ?? 0,
    groupId: groupId ?? 0,
  };
  const skipLessonQuery = !isEditSessionModalOpen || !selectedSessionForEdit || !groupId;
  const { data: fullLessonData, isLoading: isFullLessonLoading } = useGetLessonByModuleQuery(
    lessonQueryArgs,
    { skip: skipLessonQuery },
  );
  const normalizedLessonFromResponse = useMemo(
    () => fullLessonData?.data?.lesson ?? null,
    [fullLessonData],
  );
  const lessonForEdit = normalizedLessonFromResponse ?? selectedSessionForEdit;
  // Only which steps are uploading: progress ticks must not re-render the whole page
  const uploadingStepIds = useVideoUploadStepIds();
  const uploadingScormStepIds = useScormUploadStepIds();
  const isDocumentVisible = useDocumentVisibility();
  // Shares the cache entry above and keeps it fresh while content is being processed. Videos and
  // SCORM packages uploading from this tab are shown by the uploads managers until the upload is
  // accepted, so they are not polled; an unfinished upload that is not running here (another
  // tab, interrupted) is polled slowly, to notice when it moves on
  const lessonPollingInterval = useMemo(() => {
    const lesson = normalizedLessonFromResponse;
    if (!lesson) return 0;
    if (isScormProcessing(lesson.scormPackage?.processingStatus)) return CONTENT_STATUS_POLLING_MS;
    let interval = 0;
    for (const step of lesson.steps ?? []) {
      const scormStatus = step.scorm?.processingStatus;
      if (isScormProcessing(scormStatus)) return CONTENT_STATUS_POLLING_MS;
      if (scormStatus === 'uploading' && !uploadingScormStepIds.includes(step.id)) {
        interval = INTERRUPTED_UPLOAD_POLLING_MS;
      }
      const video = mapLessonVideo(step.video);
      if (video?.status !== 'pending') continue;
      if (uploadingStepIds.includes(step.id)) continue;
      if (!isVideoUploadInterrupted(video)) return CONTENT_STATUS_POLLING_MS;
      interval = INTERRUPTED_UPLOAD_POLLING_MS;
    }
    return interval;
  }, [normalizedLessonFromResponse, uploadingStepIds, uploadingScormStepIds]);
  useGetLessonByModuleQuery(lessonQueryArgs, {
    skip: skipLessonQuery || lessonPollingInterval === 0 || !isDocumentVisible,
    pollingInterval: lessonPollingInterval,
  });

  const { data: moduleBaseInfo, isLoading: isModuleBaseInfoLoading } = useGetModuleBaseInfoQuery(
    {
      intakeModuleId: moduleId,
      groupId: groupId || 0,
    },
    { skip: !groupId },
  );

  const { data: lessonsData, isLoading: isLessonsLoading } = useGetModuleLessonsQuery(
    {
      intakeModuleId: moduleId,
      groupId: groupId || 0,
    },
    { skip: !groupId },
  );

  const moduleTitle = moduleBaseInfo?.data?.module?.title || '';
  // The table renders each cell function as a component: new column definitions on a re-render
  // remount every cell, and an open actions menu closes
  const columns = useMemo(() => sessionsColumns(), []);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const sessions = useMemo<SessionRow[]>(() => {
    if (!lessonsData?.data) return [];
    const lessons: SessionRow[] = lessonsData.data.lessons.map((lesson) => ({
      ...lesson,
      _kind: 'lesson',
      order: lesson.order,
    }));
    const resubmissions: SessionRow[] = lessonsData.data.resubmissions.map((resubmission) => ({
      ...resubmission,
      _kind: 'resubmission',
      order: resubmission.order,
    }));
    const toTime = (s: SessionRow) => {
      const t = s.scheduledAt ? new Date(s.scheduledAt).getTime() : NaN;
      return Number.isNaN(t) ? Infinity : t;
    };
    const allSessions = [...lessons, ...resubmissions].sort((a, b) => {
      // Default: by session date (ascending), then by creation order
      if (sessionsSort === 'default') {
        const diff = toTime(a) - toTime(b);
        return diff !== 0 ? diff : a.order - b.order;
      }
      const [field, dir] = sessionsSort.split(':');
      const mul = dir === 'desc' ? -1 : 1;
      if (field === 'scheduledAt') return mul * (toTime(a) - toTime(b));
      if (field === 'duration') return mul * ((a.duration || 0) - (b.duration || 0));
      return 0;
    });

    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      return allSessions.filter(
        (session) =>
          session.title.toLowerCase().includes(searchLower) ||
          session.description?.toLowerCase().includes(searchLower),
      );
    }

    return allSessions;
  }, [lessonsData?.data, debouncedSearch, sessionsSort]);

  const handleExport = () => {
    // TODO: Implement export
  };

  const handleAddSession = () => {
    setIsSessionModalOpen(true);
  };

  const handleSessionModalSuccess = () => {
    setIsSessionModalOpen(false);
  };

  const handleFilterSessions = () => {
    // TODO: Implement filter sessions
  };

  const handleViewSession = (session: Session) => {
    const selected = session as SessionRow;
    if (!('id' in selected)) return;
    const previewPath = routerPath.moduleLessonPreview
      .replace(':courseId', String(courseId))
      .replace(':intakeId', String(intakeId))
      .replace(':groupNumber', String(groupNumber))
      .replace(':termId', String(termId))
      .replace(':moduleId', String(moduleId))
      .replace(':lessonId', String(selected.id));
    navigate(previewPath);
  };

  const handleMarkAttendance = (session: Session) => {
    const selected = session as SessionRow;
    if (selected._kind === 'resubmission') {
      toast({
        title: 'Unavailable',
        description: 'Attendance cannot be marked for a resubmission',
        color: 'warning',
      });
      return;
    }
    const groupLessonId = selected.groupLessonId ?? selected.id;
    if (!groupLessonId) {
      toast({
        title: 'Unavailable',
        description: 'This session has no group lesson id',
        color: 'warning',
      });
      return;
    }
    const attendancePath = routerPath.sessionAttendance
      .replace(':courseId', String(courseId))
      .replace(':intakeId', String(intakeId))
      .replace(':groupNumber', String(groupNumber))
      .replace(':termId', String(termId))
      .replace(':moduleId', String(moduleId))
      .replace(':groupLessonId', String(groupLessonId));
    navigate(attendancePath);
  };

  const handleEditSession = (session: Session) => {
    const selected = session as SessionRow;
    if (selected._kind === 'resubmission') {
      toast({
        title: 'Unavailable',
        description: 'Resubmission cannot be edited from this list',
        color: 'warning',
      });
      return;
    }
    setSelectedSessionForEdit(session as Lesson);
    setIsEditSessionModalOpen(true);
  };

  const handleEditSessionSuccess = () => {
    setIsEditSessionModalOpen(false);
    setSelectedSessionForEdit(null);
  };

  const handleDuplicateSession = () => {
    // TODO: Implement duplicate session
  };

  const handleDeleteSession = (session: Session) => {
    const selected = session as SessionRow;
    if (selected._kind === 'resubmission') {
      toast({
        title: 'Unavailable',
        description: 'Resubmission cannot be deleted from this list',
        color: 'warning',
      });
      return;
    }
    setSelectedSessionForDelete(session as Lesson);
    onDeleteModalOpen();
  };

  const handleConfirmDelete = async () => {
    if (!selectedSessionForDelete) return;

    try {
      await deleteLesson(selectedSessionForDelete.id).unwrap();
      toast({
        title: 'Success',
        description: 'Session deleted successfully',
        color: 'success',
      });
      setSelectedSessionForDelete(null);
      onDeleteModalOpenChange();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to delete session',
        color: 'danger',
      });
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedSessions.length === 0) return;
    try {
      await Promise.all(selectedSessions.map((s) => deleteLesson(s.id).unwrap()));
      toast({
        title: 'Success',
        description: `${selectedSessions.length} session(s) deleted successfully`,
        color: 'success',
      });
      setSelectedSessions([]);
      onBulkDeleteModalOpenChange();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to delete selected sessions',
        color: 'danger',
      });
      onBulkDeleteModalOpenChange();
    }
  };

  if (isTermsLoading || isLessonsLoading || isModuleBaseInfoLoading) {
    return <SectionLoader />;
  }

  if (!term || !lessonsData?.data || !moduleBaseInfo?.data) {
    return null;
  }

  const courseTitle = moduleBaseInfo.data.course?.title || termsData?.data?.course?.title || '';
  const intakeTitle = moduleBaseInfo.data.intake?.title || termsData?.data?.intake?.title || '';
  const intakeGroupLabel = intakeTitle
    ? `${intakeTitle} - Group ${groupNumber}`
    : `Group ${groupNumber}`;
  const termNumber = moduleBaseInfo.data.term?.number ?? term?.number;
  const groupDetailPath = routerPath.groupDetail
    .replace(':courseId', String(courseId))
    .replace(':intakeId', String(intakeId))
    .replace(':groupNumber', String(groupNumber));
  const termDetailPath = routerPath.termDetail
    .replace(':courseId', String(courseId))
    .replace(':intakeId', String(intakeId))
    .replace(':groupNumber', String(groupNumber))
    .replace(':termId', String(termId));

  const breadcrumbItems = [
    { label: 'Courses', href: routerPath.courses },
    {
      label: courseTitle || 'Course',
      href: routerPath.courseDetail.replace(':id', String(courseId)),
    },
    { label: intakeGroupLabel, href: groupDetailPath },
    { label: `Term ${termNumber}`, href: termDetailPath },
    { label: moduleTitle || 'Module' },
  ];

  return (
    <div className={styles.root}>
      <Container className="flex flex-col gap-6">
        <CourseBreadcrumbs items={breadcrumbItems} />

        <div className="flex items-center justify-between">
          <div className="inline-flex gap-x-2 items-end">
            <h2 className="text-[32px] font-medium">{moduleTitle}</h2>
            <p className="text-grey-secondary leading-[220%] min-w-[180px]">
              (found {sessions.length} sessions)
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between gap-4">
            <div className="w-full max-w-[530px]">
              <CustomInput
                className={searchInputClassName}
                startContent={<Svg Icon={SearchIcon} />}
                placeholder="Search"
                value={search}
                onValueChange={setSearch}
              />
            </div>
            <div className="flex items-center gap-4">
              {canSessionCreate && (
                <Button
                  className="bg-[#211D55] text-white font-medium font-base rounded-lg"
                  onPress={handleAddSession}
                >
                  Add new session
                </Button>
              )}
              <Button
                variant="bordered"
                className="border-[#211D55] bg-white font-medium font-base text-[#211D55] min-w-[152px] rounded-lg"
                onPress={handleExport}
              >
                Export
              </Button>
            </div>
          </div>

          <Divider />

          <div className="flex flex-col gap-4">
            <div className="flex justify-between">
              <Button isIconOnly className="bg-brand rounded" onPress={handleFilterSessions}>
                <Svg Icon={FilterIcon} className="text-white" />
              </Button>
              <SelectSort
                sections={sessionsSortOptions}
                defaultValue={sessionsSort}
                onSelectionChange={(keys) => {
                  const value = (new Set(keys).values().next().value as string) || 'default';
                  setSessionsSort(value);
                }}
              />
            </div>

            {selectedSessions.length > 0 && (
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-grey-secondary">
                  Selected: {selectedSessions.length}
                </span>
                <div className="flex items-center gap-4">
                  <Button
                    variant="light"
                    isDisabled
                    className="text-base text-grey-secondary"
                    endContent={<Svg Icon={MoveIcon} width={20} height={20} />}
                  >
                    Remove ({selectedSessions.length})
                  </Button>
                  <Button
                    variant="light"
                    isDisabled
                    className="text-base text-grey-description"
                    endContent={<Svg Icon={OpenedLockIcon} width={20} height={20} />}
                  >
                    Block selected ({selectedSessions.length})
                  </Button>
                  {canSessionDelete && (
                    <Button
                      variant="light"
                      className="text-base text-red-tertiary"
                      endContent={<Svg Icon={TrashIcon} width={20} height={20} />}
                      onPress={onBulkDeleteModalOpen}
                    >
                      Delete ({selectedSessions.length})
                    </Button>
                  )}
                </div>
              </div>
            )}

            <ContainerTable<SessionRow>
              data={sessions}
              columns={columns}
              pagination={pagination}
              setPagination={setPagination as OnChangeFn<PaginationState>}
              hideTopActions={true}
              onSelectedItemsChange={setSelectedSessions}
              tableMeta={{
                onView: handleViewSession,
                onMarkAttendance: canMarkAttendance ? handleMarkAttendance : undefined,
                onEdit: canSessionUpdate ? handleEditSession : undefined,
                onDuplicate: canSessionCreate ? handleDuplicateSession : undefined,
                onDelete: canSessionDelete ? handleDeleteSession : undefined,
              }}
            />
          </div>
        </div>
      </Container>

      {groupId && (
        <AddSessionModal
          isOpen={isSessionModalOpen}
          onOpenChange={setIsSessionModalOpen}
          intakeModuleId={moduleId}
          groupId={groupId}
          intakeTerm={moduleBaseInfo.data.term}
          onSuccess={handleSessionModalSuccess}
        />
      )}
      {selectedSessionForEdit && groupId && (
        <EditSessionModal
          isOpen={isEditSessionModalOpen}
          onOpenChange={setIsEditSessionModalOpen}
          lesson={lessonForEdit ?? selectedSessionForEdit}
          groupId={groupId}
          intakeTerm={moduleBaseInfo.data.term}
          isLessonLoading={isFullLessonLoading}
          onSuccess={handleEditSessionSuccess}
        />
      )}
      {selectedSessionForDelete && (
        <MessageModal
          title="Delete Session"
          text={`Are you sure you want to delete the session "${selectedSessionForDelete.title}"? This action cannot be undone.`}
          confirmText="Delete"
          isOpen={isDeleteModalOpen}
          onOpenChange={onDeleteModalOpenChange}
          variant="red"
          toggleVisibility={handleConfirmDelete}
          isLoading={isDeleting}
        />
      )}

      <MessageModal
        title="Delete Selected Sessions"
        text={`Are you sure you want to delete ${selectedSessions.length} selected session(s)? This action cannot be undone.`}
        confirmText="Delete"
        isOpen={isBulkDeleteModalOpen}
        onOpenChange={onBulkDeleteModalOpenChange}
        variant="red"
        toggleVisibility={handleConfirmBulkDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};
